import {
  ConversationEscalationResponse,
  type ConversationResponse
} from "@9amhealth/openapi";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import createTrackEvent from "src/lib/createTrackEvent";
import { toast, tracker } from "src/state/state";
import { ConversationService } from "./ConversationService";
import {
  ChatEventType,
  type ChatPrompt,
  type ChatPromptOption,
  type LeapChatMessage
} from "./types";
import { getLatestResolvedEscalationAt } from "./utils";

export interface ChatPromptManagerDeps {
  service: ConversationService;
  getMessages: () => LeapChatMessage[];
  getConversation: () => ConversationResponse | null;
  getPrompts: () => Record<string, ChatPrompt>;
  onPromptsChange: (prompts: Record<string, ChatPrompt>) => void;
  onEscalationChange: (
    escalated: boolean,
    bannerIndex: number | null,
    latestResolvedEscalationAt: number | null
  ) => void;
}

export default class ChatPromptManager {
  private deps: ChatPromptManagerDeps;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private promptRegistry: Record<
    string,
    | {
        options: ChatPromptOption[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSelect: (value: string, eventData: any) => void;
      }
    | undefined
  > = {
    [ChatEventType.EscalationPrompt]: {
      options: [
        {
          label: translate("leapChat.escalation.confirm"),
          value: "confirm",
          variant: "primary"
        },
        {
          label: translate("leapChat.escalation.cancel"),
          value: "cancel",
          variant: "secondary"
        }
      ],
      onSelect: (
        ...args: Parameters<typeof this.handleSelectEscalationPromptOption>
      ) => this.handleSelectEscalationPromptOption(...args)
    }
  };

  constructor(deps: ChatPromptManagerDeps) {
    this.deps = deps;
  }

  showPrompt = (
    eventType: string,
    eventData: unknown,
    messageId: string,
    conversationId: string
  ): void => {
    const entry = this.promptRegistry[eventType];
    if (!entry) {
      return;
    }
    const prompt: ChatPrompt = {
      id: crypto.randomUUID(),
      messageId,
      conversationId,
      eventType,
      eventData,
      options: entry.options
    };
    this.deps.onPromptsChange({
      ...this.deps.getPrompts(),
      [messageId]: prompt
    });
  };

  selectPromptOption = (messageId: string, value: string): void => {
    const prompts = this.deps.getPrompts();
    const prompt = prompts[messageId];
    if (!prompt) {
      return;
    }

    const entry = this.promptRegistry[prompt.eventType];
    entry?.onSelect(value, prompt.eventData);

    const { [messageId]: _, ...rest } = prompts;
    this.deps.onPromptsChange(rest);
  };

  buildPromptsFromMessages(
    messages: LeapChatMessage[],
    conversationId: string
  ): Record<string, ChatPrompt> {
    const prompts: Record<string, ChatPrompt> = {};
    for (const msg of messages) {
      if (msg.escalationPrompted) {
        const entry = this.promptRegistry[ChatEventType.EscalationPrompt];
        if (entry) {
          prompts[msg.id] = {
            id: msg.id,
            messageId: msg.id,
            conversationId,
            eventType: ChatEventType.EscalationPrompt,
            eventData: { conversationId },
            options: entry.options
          };
        }
      }
    }
    return prompts;
  }

  updateEscalationState(): void {
    const conv = this.deps.getConversation();
    if (!conv) {
      this.deps.onEscalationChange(false, null, null);
      return;
    }

    const escalated = ConversationService.isEscalated(conv);

    const escalation = conv.escalations.find(
      (e) => e.status === ConversationEscalationResponse.status.OPEN
    );
    const ts = escalation ? new Date(escalation.createdAt).getTime() : null;

    let escalationBannerIndex: number | null = null;
    if (ts !== null) {
      const messages = this.deps.getMessages();
      const idx = messages.findIndex((m) => m.createdAt > ts);

      if (idx === -1) {
        escalationBannerIndex = messages.length;
      } else if (idx > 0) {
        escalationBannerIndex = idx;
      }
    }

    const latestResolvedEscalationAt = getLatestResolvedEscalationAt(
      conv.escalations
    );
    this.deps.onEscalationChange(
      escalated,
      escalationBannerIndex,
      latestResolvedEscalationAt
    );
  }

  handleExternalEscalation = (conversationId: string): void => {
    const prompts = this.deps.getPrompts();
    const remaining = Object.fromEntries(
      Object.entries(prompts).filter(
        ([, p]) => p.eventType !== ChatEventType.EscalationPrompt
      )
    );
    if (Object.keys(remaining).length !== Object.keys(prompts).length) {
      this.deps.onPromptsChange(remaining);
    }

    toast.success("leapChat.escalation.success");
    this.deps.service
      .listConversations()
      .then(() => {
        this.updateEscalationState();
      })
      .catch(reportErrorSentry);
  };

  private handleSelectEscalationPromptOption = (
    value: string,
    eventData: { conversationId: string }
  ): void => {
    if (value !== "confirm") {
      tracker.track(createTrackEvent("Escalation Cancelled"), {
        data: { "Conversation ID": eventData.conversationId }
      });
      return;
    }

    this.deps.service
      .createEscalation(eventData.conversationId)
      .then((res) => {
        toast.success("leapChat.escalation.success");
        const nowIso = new Date().toISOString();
        this.deps.service.updateConversation(eventData.conversationId, (c) => ({
          ...c,
          escalations: [
            ...c.escalations,
            {
              id: res.escalationId,
              status: ConversationEscalationResponse.status.OPEN,
              createdAt: nowIso,
              updatedAt: nowIso
            }
          ]
        }));
        this.updateEscalationState();
        void this.deps.service.listConversations();
      })
      .catch((e) => {
        toast.error("leapChat.escalation.error");
        reportErrorSentry(e);
      });
  };
}
